import React, { useEffect, useState } from 'react';
import GenericModal from '~/components/GenericModal/GenericModal';
import Spinner from '~/components/Spinner/Spinner';
import {
  AuditBatchResponse,
  DeliveryCounts,
  getNotificationAuditBatch,
  ReasonCounts
} from '~/api/notificationAudit';
import classes from './NotificationAudit.module.css';

interface IProps {
  batchUuid: string | null;
  onClose: () => void;
}

export const sumCounts = (c: DeliveryCounts) => ({
  sent: c.convertedSent + c.provisionedSent,
  delivered: c.convertedDelivered + c.provisionedDelivered,
  rejected: c.convertedRejected + c.provisionedRejected,
  noStatus: c.convertedNoStatus + c.provisionedNoStatus
});

export const pct = (part: number, whole: number) =>
  whole ? `${Math.round((part / whole) * 100)}%` : '—';

const appName = (url: string | null) =>
  url ? url.replace(/^https?:\/\//, '').replace(/\/.*$/, '') : '—';

// What each Homebody rejection reason means. Unknown codes fall back to the raw code.
export const REASON_MEANINGS: Record<string, string> = {
  NO_DEVICES:
    'Resident has no phone registered for push in this app (never installed it, or never allowed notifications).',
  NO_SUBSCRIBED_DEVICES:
    'Resident has a phone registered, but notifications are turned off on it.',
  BATCH_ERROR:
    'Entrata failed the whole batch this resident was in (error or timeout on their side). Nobody in that batch got it.',
  API_ERROR:
    'The call to Entrata failed outright for this push — no resident in it was reached.',
  UNKNOWN: 'Entrata rejected the resident without giving a reason.',
  UNKNOWN_ERROR: 'Entrata reported an unspecified error for this resident.'
};

const ReasonLegend = () => (
  <details style={{ marginTop: 8 }}>
    <summary className={classes.subtle} style={{ cursor: 'pointer' }}>
      All rejection reasons explained
    </summary>
    <table className={classes.table}>
      <tbody>
        {Object.entries(REASON_MEANINGS).map(([code, meaning]) => (
          <tr key={code}>
            <td style={{ whiteSpace: 'nowrap' }}>{code}</td>
            <td className={classes.reasonMeaning}>{meaning}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </details>
);

const ReasonTable = ({
  reasons
}: {
  reasons: { converted: ReasonCounts; provisioned: ReasonCounts };
}) => {
  const keys = Array.from(
    new Set([
      ...Object.keys(reasons.converted),
      ...Object.keys(reasons.provisioned)
    ])
  ).sort(
    (a, b) =>
      (reasons.converted[b] || 0) +
      (reasons.provisioned[b] || 0) -
      ((reasons.converted[a] || 0) + (reasons.provisioned[a] || 0))
  );
  if (!keys.length) return <p className={classes.subtle}>No rejections.</p>;
  return (
    <table className={classes.table}>
      <thead>
        <tr>
          <th>Reason</th>
          <th>Meaning</th>
          <th className={classes.num}>Cobu account</th>
          <th className={classes.num}>Roster-only</th>
          <th className={classes.num}>Total</th>
        </tr>
      </thead>
      <tbody>
        {keys.map((k) => (
          <tr key={k}>
            <td style={{ whiteSpace: 'nowrap' }}>{k}</td>
            <td className={classes.reasonMeaning}>
              {REASON_MEANINGS[k] || 'Reason code returned by Entrata.'}
            </td>
            <td className={classes.num}>{reasons.converted[k] || 0}</td>
            <td className={classes.num}>{reasons.provisioned[k] || 0}</td>
            <td className={classes.num}>
              {(reasons.converted[k] || 0) + (reasons.provisioned[k] || 0)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const SplitTable = ({ t }: { t: DeliveryCounts }) => {
  const rows = [
    {
      label: 'Cobu account',
      s: t.convertedSent,
      d: t.convertedDelivered,
      r: t.convertedRejected,
      n: t.convertedNoStatus
    },
    {
      label: 'Roster-only',
      s: t.provisionedSent,
      d: t.provisionedDelivered,
      r: t.provisionedRejected,
      n: t.provisionedNoStatus
    }
  ];
  return (
    <table className={classes.table}>
      <thead>
        <tr>
          <th>Residents</th>
          <th className={classes.num}>Sent to partner</th>
          <th className={classes.num}>Delivered</th>
          <th className={classes.num}>Rejected</th>
          <th className={classes.num}>No final status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((x) => (
          <tr key={x.label}>
            <td>{x.label}</td>
            <td className={classes.num}>{x.s}</td>
            <td className={classes.num}>
              {x.d}{' '}
              <span className={classes.subtle}>({pct(x.d, x.d + x.r)})</span>
            </td>
            <td className={classes.num}>{x.r}</td>
            <td className={classes.num}>{x.n}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const NotificationAuditResultModal = ({ batchUuid, onClose }: IProps) => {
  const [data, setData] = useState<AuditBatchResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!batchUuid) return;
    setData(null);
    setError(null);
    getNotificationAuditBatch(batchUuid)
      .then(({ data }) => setData(data))
      .catch((e) =>
        setError(
          e?.response?.data?.message || e?.message || 'Could not load result.'
        )
      );
  }, [batchUuid]);

  let content: React.ReactNode = (
    <div style={{ textAlign: 'center', padding: 24 }}>
      <Spinner />
    </div>
  );
  if (error) content = <p className={classes.bad}>{error}</p>;
  if (data) {
    const s = sumCounts(data.totals);
    const buildings = [...data.buildings].sort(
      (a, b) =>
        sumCounts(a).delivered /
          Math.max(1, sumCounts(a).delivered + sumCounts(a).rejected) -
        sumCounts(b).delivered /
          Math.max(1, sumCounts(b).delivered + sumCounts(b).rejected)
    );
    content = (
      <div className={classes.modalBody}>
        <p className={classes.postText} style={{ maxWidth: 'none' }}>
          {data.postText || '—'}
        </p>
        <p className={classes.subtle}>
          {data.isBulk
            ? `Multi-building post · ${data.buildings.length} buildings`
            : 'Single post'}{' '}
          · started {new Date(data.startedAt).toLocaleString()}
          {data.endedAt &&
            ` · finished ${new Date(data.endedAt).toLocaleString()}`}
          {data.isBackfilled &&
            ' · approximate (from history, before tracking started)'}
        </p>

        <div className={classes.cards}>
          <div className={classes.card}>
            <div className={classes.subtle}>Sent to partner</div>
            <div className={classes.cardValue}>{s.sent}</div>
          </div>
          <div className={classes.card}>
            <div className={classes.subtle}>Delivered by partner</div>
            <div className={`${classes.cardValue} ${classes.good}`}>
              {s.delivered}{' '}
              <span className={classes.subtle}>
                {pct(s.delivered, s.delivered + s.rejected)}
              </span>
            </div>
          </div>
          <div className={classes.card}>
            <div className={classes.subtle}>Rejected</div>
            <div className={`${classes.cardValue} ${classes.bad}`}>
              {s.rejected}
            </div>
          </div>
          <div className={classes.card}>
            <div className={classes.subtle}>No final status</div>
            <div className={classes.cardValue}>{s.noStatus}</div>
          </div>
        </div>

        <div className={classes.section}>Who received it</div>
        <SplitTable t={data.totals} />

        <div className={classes.section}>Why rejected</div>
        <ReasonTable reasons={data.reasons} />
        <ReasonLegend />

        {data.isBulk && (
          <>
            <div className={classes.section}>
              By building (lowest delivery first)
            </div>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th>Building</th>
                  <th>App</th>
                  <th className={classes.num}>Sent</th>
                  <th className={classes.num}>Delivered</th>
                  <th className={classes.num}>Rejected</th>
                  <th className={classes.num}>No status</th>
                </tr>
              </thead>
              <tbody>
                {buildings.map((b) => {
                  const c = sumCounts(b);
                  return (
                    <tr key={b.building}>
                      <td>{b.buildingName || b.building}</td>
                      <td className={classes.subtle}>{appName(b.appUrl)}</td>
                      <td className={classes.num}>{c.sent}</td>
                      <td className={classes.num}>
                        {c.delivered}{' '}
                        <span className={classes.subtle}>
                          ({pct(c.delivered, c.delivered + c.rejected)})
                        </span>
                      </td>
                      <td className={classes.num}>{c.rejected}</td>
                      <td className={classes.num}>{c.noStatus}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </div>
    );
  }

  return (
    <GenericModal
      showModal={!!batchUuid}
      title='Push result'
      content={content}
      hideActionButton
      cancelOverride='Close'
      onChange={(show) => !show && onClose()}
      width='900px'
    />
  );
};

export default NotificationAuditResultModal;
